/**
 * Types for Browser Automation UI components.
 */

/**
 * Step type variants for the StepTypeIndicator component.
 */
export enum EStepType {
  GOAL = 'goal',
  NAVIGATE = 'navigate',
  CLICK = 'click',
  TYPE = 'type',
  THOUGHT = 'thought',
  OUTPUT = 'output',
}

/**
 * Browser step status variants.
 */
export enum EBrowserStepStatus {
  COMPLETE = 'complete',
  CURRENT = 'current',
  PLANNED = 'planned',
}

/**
 * Browser preview FAB state variants.
 */
export enum EBrowserPreviewFabState {
  PLAYING = 'playing',
  PAUSED = 'paused',
  REPLAY = 'replay',
}

/**
 * Browser full preview state variants. Lifecycle: LOADING → LIVE → REPLAY.
 * ENG-3731 tracks a shared `getBrowserPlayerState` helper to derive this
 * from run-lifecycle signals; today BrowserRenderer derives state inline.
 */
export enum EBrowserPlayerState {
  LOADING = 'loading',
  LIVE = 'live',
  REPLAY = 'replay',
}

/**
 * Agent execution status reported by the browser farm WebSocket.
 */
export enum EAgentStatus {
  RUNNING = 'running',
  COMPLETED = 'completed',
  ERROR = 'error',
  PAUSED = 'paused',
  STOPPED = 'stopped',
  IDLE = 'idle',
}

/**
 * A single step performed by the browser automation agent.
 *
 * Shared shape across live (SSE step stream) and replay (metadata.json)
 * data sources, so one component can render both. Replay-only fields are
 * optional.
 */
export interface IAgentStep {
  /** Sequential step number. */
  step_number: number;
  /** Human-readable description of what the agent did. */
  description: string;
  /** ISO 8601 timestamp of when the step occurred. */
  timestamp: string;
  /**
   * (Replay only) Offset into the encoded mp4 where this step begins, in
   * seconds. Derived from cumulative frame_count; clicking the step in the
   * sidebar seeks the video to this value. Absent for live (SSE) steps.
   */
  video_offset_seconds?: number;
  /**
   * (Replay only) Whether the action succeeded. Live steps don't carry this
   * field — the agent broadcasts the step_intent before action completion.
   */
  success?: boolean;
  /**
   * (Replay only) Executor action name, e.g. `navigate` or `click`. Picks the
   * step's icon via {@link getStepTypeFromAction}.
   */
  action_type?: string;
}

/**
 * WebSocket message received from the agent stream endpoint.
 */
export interface IAgentStreamMessage {
  /** The type of message. */
  type: 'history' | 'step' | 'status' | 'error';
  /** Message payload (shape depends on type). */
  data: {
    /** Agent status (for history and status messages). */
    status?: string;
    /** Historical steps (for history messages). */
    steps?: IAgentStep[];
    /** Step number (for step messages). */
    step_number?: number;
    /** Step description (for step messages). */
    description?: string;
    /** Step timestamp (for step messages). */
    timestamp?: string;
    /** Error description (for status messages). */
    error?: string;
    /** Error message (for error messages). */
    message?: string;
  };
}
